import type { YearlyMonth } from '@/entities/performance/api/performance.api';
import {
  useAvailableYears,
  useYearlyPerformance,
} from '@/entities/performance/hooks/usePerformanceQueries';
import { getAnnualKpis } from '@/entities/performance/lib/dashboard';
import {
  formatChange,
  formatRatio,
  monthOf,
  toYm,
} from '@/entities/performance/lib/performance';
import {
  EmptyState,
  ErrorState,
  LoadingBlock,
} from '@/shared/components/StateViews';
import { Button } from '@/shared/components/ui/button';
import { Icon } from '@/shared/components/ui/icon';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { formatWon } from '@/shared/utils/format';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useMemo } from 'react';
import { KpiItem, KpiRow } from './components/Kpi';
import { deltaClass } from './lib';
import { MonthPanel } from './components/MonthPanel';
import {
  ChartLegend,
  PerformanceChart,
  type ChartMonth,
} from './components/PerformanceChart';

/** 연간 응답 → 그래프 값 (미등록 달은 0 위치 · 점 없음, 막대는 정산 완료 달만) */
const toChartMonths = (months: YearlyMonth[]): ChartMonth[] =>
  months.map((m) => {
    const unregistered = m.monthlyStatusCode === 'UNREGISTERED';
    return {
      month: monthOf(m.settlementYearMonth),
      estimated: unregistered ? 0 : m.estimatedAmountPrescription,
      confirmed:
        m.monthlyStatusCode === 'SETTLED' ? m.confirmedAmountSettlement : null,
      unregistered,
    };
  });

/**
 * M-08 실적 대시보드 (연간) — Figma 495:6635 · 케이스 870:11834 / 587:813 / 870:11430
 * API: GET /web/performance/available-years, /yearly?year= (+ 전년 /yearly 비교)
 * - 지표 4개: 연간 총 처방 · 연간 정산 확정(전년 대비) · 평균 정산 비율 · 월 평균 정산 금액
 * - 실적 금액 추이: 막대 = 정산(확정), 점선 = 처방(예상). 호버 툴팁, 월을 누르면 아래 선택 월 패널이 바뀐다
 * - 선택 월은 URL ?month= 로 둔다 (기본: 마지막 정산 완료 달, 없으면 마지막 달)
 * 기준일 · 연간 엑셀은 서버 값이 없어 그리지 않는다 (docs/api B2 · B4)
 */
export const PerformancePage = () => {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as {
    year?: number;
    month?: number;
  };
  const yearsQuery = useAvailableYears();
  const years = useMemo(
    () => [...(yearsQuery.data ?? [])].sort((a, b) => b - a),
    [yearsQuery.data],
  );
  const year =
    search.year && years.includes(search.year) ? search.year : years[0];
  const latestYear = years[0];

  const yearlyQuery = useYearlyPerformance(year);
  const prevYear = year && years.includes(year - 1) ? year - 1 : undefined;
  const prevYearly = useYearlyPerformance(prevYear);
  const months = useMemo(
    () => yearlyQuery.data?.months ?? [],
    [yearlyQuery.data],
  );
  const lastMonth = Math.max(
    0,
    ...months.map((m) => monthOf(m.settlementYearMonth)),
  );
  const defaultMonth =
    [...months].reverse().find((m) => m.monthlyStatusCode === 'SETTLED')
      ?.settlementYearMonth ?? months[months.length - 1]?.settlementYearMonth;
  const selectedMonth =
    search.month && search.month >= 1 && search.month <= lastMonth
      ? search.month
      : defaultMonth
        ? monthOf(defaultMonth)
        : undefined;
  const selectedYm =
    year && selectedMonth ? toYm(year, selectedMonth) : undefined;
  const selected = months.find((m) => m.settlementYearMonth === selectedYm);
  // 현재 정산월 = 가장 최근 연도의 마지막 조회 가능 달
  const isCurrent = year === latestYear && selectedMonth === lastMonth;

  const go = (next: { year?: number; month?: number }) =>
    void navigate({
      to: ROUTE_PATHS.PERFORMANCE,
      search: next,
      replace: true,
    });

  /** 선택 월 패널의 월 이동 — 해를 넘으면 연도도 바꾼다 */
  const moveMonth = (diff: -1 | 1) => {
    if (!year || !selectedMonth) return;
    const m = selectedMonth + diff;
    if (m >= 1 && m <= lastMonth) return go({ year, month: m });
    if (m < 1 && years.includes(year - 1))
      return go({ year: year - 1, month: 12 });
    if (m > 12 && years.includes(year + 1))
      return go({ year: year + 1, month: 1 });
  };
  const canPrev =
    !!selectedMonth &&
    (selectedMonth > 1 || (!!year && years.includes(year - 1)));
  const canNext =
    !!selectedMonth &&
    (selectedMonth < lastMonth ||
      (selectedMonth === 12 && !!year && years.includes(year + 1)));

  const kpis = yearlyQuery.data
    ? getAnnualKpis(yearlyQuery.data, prevYearly.data)
    : undefined;

  const header = (
    <div className="flex h-8 items-center justify-between">
      <div className="flex items-center">
        <Button
          variant="text"
          size="small"
          aria-label="이전 연도"
          disabled={!year || !years.includes(year - 1)}
          onClick={() => year && go({ year: year - 1 })}
          leftIcon={<Icon name="page-left-solid-16" />}
        />
        <h1 className="text-heading-24-b text-fg-primary">
          {year ? `${year}년` : '실적'}
        </h1>
        <Button
          variant="text"
          size="small"
          aria-label="다음 연도"
          disabled={!year || !years.includes(year + 1)}
          onClick={() => year && go({ year: year + 1 })}
          leftIcon={<Icon name="page-right-solid-16" />}
        />
      </div>
      {/* TODO(server): 기준일 필드가 없어 "기준일 YYYY.MM.DD"를 그리지 않는다 (docs/api B4) */}
    </div>
  );

  if (yearsQuery.isError || yearlyQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <ErrorState
          onRetry={() => {
            void yearsQuery.refetch();
            void yearlyQuery.refetch();
          }}
        />
      </div>
    );
  }

  if (yearsQuery.isSuccess && years.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <EmptyState message="아직 실적이 없어요. 처방을 등록하면 이곳에 실적이 쌓여요." />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {header}

      <section className="flex flex-col gap-10 rounded-20 bg-surface-white p-6">
        {!kpis ? (
          <LoadingBlock className="h-[130px]" />
        ) : (
          <KpiRow
            className="rounded-12 bg-surface-grey"
            items={[
              <KpiItem
                key="rx"
                label="연간 총 처방 금액"
                value={formatWon(kpis.prescription)}
                sub={
                  kpis.prescriptionYoY !== null &&
                  `전년 대비 ${formatChange(kpis.prescriptionYoY)}`
                }
                subClassName={deltaClass(kpis.prescriptionYoY)}
              />,
              <KpiItem
                key="confirmed"
                label="연간 정산 확정 금액"
                value={formatWon(kpis.confirmed)}
                sub={
                  kpis.confirmedYoY !== null &&
                  `전년 대비 ${formatChange(kpis.confirmedYoY)}`
                }
                subClassName={deltaClass(kpis.confirmedYoY)}
              />,
              <KpiItem
                key="ratio"
                label="평균 정산 비율"
                value={formatRatio(kpis.ratio)}
              />,
              <KpiItem
                key="avg"
                label="월 평균 정산 금액"
                value={
                  kpis.monthlyAverage === null
                    ? '-'
                    : formatWon(kpis.monthlyAverage)
                }
                sub={
                  kpis.settledMonthCount > 0 &&
                  `정산 완료 ${kpis.settledMonthCount}개월 기준`
                }
              />,
            ]}
          />
        )}

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-x-2">
              <h2 className="text-subtitle-18-b text-fg-primary">
                실적 금액 추이
              </h2>
              <p className="text-body-13-r text-fg-tertiary">
                확정 금액이 예상금액보다 많을 수 있어요. 최소금액을 못 채워도
                제약사가 승인하면 정산돼요.
              </p>
            </div>
            <ChartLegend />
          </div>
          {yearlyQuery.isPending ? (
            <LoadingBlock className="h-[224px]" />
          ) : (
            <div className="overflow-x-auto">
              <PerformanceChart
                className="min-w-[720px]"
                months={toChartMonths(months)}
                lastMonth={lastMonth}
                selectedMonth={selectedMonth}
                onSelect={(m) => go({ year, month: m })}
              />
            </div>
          )}
        </div>
      </section>

      {selectedYm && (
        <MonthPanel
          key={selectedYm}
          ym={selectedYm}
          status={selected?.monthlyStatusCode}
          current={isCurrent}
          canPrev={canPrev}
          canNext={canNext}
          onMove={moveMonth}
        />
      )}
    </div>
  );
};
